import { useCheckoutForm } from '../checkout/store'

/**
 * The nav cart icon — LoginButton's sibling, wired to the checkout draft the
 * way LoginButton is wired to the session.
 *
 * The "cart" is the checkout form's draft: one kit/subscription per
 * configured child. The module-scope store hydrates from the sessionStorage
 * draft at bundle load, so the badge is correct on every page of the site,
 * and updates live on the checkout page itself (separate React roots, one
 * store). Clicking always navigates to the pre-checkout page, which restores
 * the visitor's progress from the same draft.
 */

export interface CartButtonProps {
  /** The page hosting the Checkout component. `data-checkout-url` in Webflow. */
  checkoutUrl?: string
}

export function CartButton({ checkoutUrl = '/checkout' }: CartButtonProps) {
  // `started` rather than a bare child count: the store's default state holds
  // one empty child, and a visitor who never touched checkout must not see a
  // phantom "1" in the nav.
  const started = useCheckoutForm((state) => state.started)
  const count = useCheckoutForm((state) => state.children.length)

  const label = started ? `Cart, ${count} ${count === 1 ? 'item' : 'items'}` : 'Cart'

  return (
    <a
      href={checkoutUrl}
      aria-label={label}
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-ink outline-none transition hover:bg-brand-soft hover:text-brand focus-visible:ring-2 focus-visible:ring-brand-ring"
      data-testid="cart-link"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="none">
        <path
          d="M3 4h2.2l2.3 11.2a1.6 1.6 0 0 0 1.57 1.3h8.13a1.6 1.6 0 0 0 1.56-1.23L20.5 8.5H6.1"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="9.9" cy="19.9" r="1.4" fill="currentColor" />
        <circle cx="16.7" cy="19.9" r="1.4" fill="currentColor" />
      </svg>

      {started && (
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1 text-xs font-bold text-white"
          data-testid="cart-count"
        >
          {count}
        </span>
      )}
    </a>
  )
}
